import { Suspense, lazy, useEffect, useRef, useState } from "react";
import DemoModalProvider from "@/components/ui/demo-provider";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Hero from "@/components/home/Hero";

/** Below-fold sections - keep Hero + Nav immediate for LCP. */
const Platform = lazy(() => import("@/components/home/Platform"));
const PayrollOps = lazy(() => import("@/components/home/PayrollOps"));
const MonthlyAnalysis = lazy(() => import("@/components/home/MonthlyAnalysis"));
const MonthEndPack = lazy(() => import("@/components/home/MonthEndPack"));
const Workforce = lazy(() => import("@/components/home/Workforce"));
const SystemDiagram = lazy(() => import("@/components/home/SystemDiagram"));
const Security = lazy(() => import("@/components/home/Security"));
const Spectrum = lazy(() => import("@/components/home/Spectrum"));
const CTABand = lazy(() => import("@/components/home/CTABand"));

/** Transparent placeholder - never paint an opaque plate (avoids colour flashes on lazy mount). */
function SectionFallback({ minH = "40vh" }) {
  return <div style={{ minHeight: minH }} aria-hidden="true" />;
}

/**
 * Mount lazy section only when near viewport (keeps Hero LCP clean).
 * Optional `atmosphere` is always mounted on the stable wrapper so transition
 * washes exist before the section chunk loads.
 */
function LazySection({ children, minH = "50vh", rootMargin = "480px 0px", id, atmosphere }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || ready) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setReady(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setReady(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ready, rootMargin]);

  // Hash / in-page nav: mount immediately so the target exists to scroll to.
  useEffect(() => {
    if (ready || !id) return undefined;
    const maybeLoad = () => {
      if (window.location.hash === `#${id}`) setReady(true);
    };
    maybeLoad();
    window.addEventListener("hashchange", maybeLoad);
    return () => window.removeEventListener("hashchange", maybeLoad);
  }, [id, ready]);

  return (
    <div ref={ref} id={id} className="relative">
      {atmosphere}
      <div className="relative z-[1]">
        {ready ? (
          <Suspense fallback={<SectionFallback minH={minH} />}>{children}</Suspense>
        ) : (
          <SectionFallback minH={minH} />
        )}
      </div>
    </div>
  );
}

function Home() {
  return (
    <main className="relative">
      {/* Shared page base is body --bg-primary; hero canvas fades into Platform */}
      <Hero />
      <LazySection id="platform" minH="70vh" rootMargin="600px 0px">
        <Platform />
      </LazySection>

      <LazySection id="payroll" minH="70vh">
        <PayrollOps />
      </LazySection>

      <LazySection id="analysis" minH="80vh">
        <MonthlyAnalysis />
      </LazySection>

      <LazySection id="month-end" minH="90vh">
        <MonthEndPack />
      </LazySection>

      {/* Workforce teal field bleeds into Month End and Architecture — radials only, no plate */}
      <LazySection
        id="workforce"
        minH="90vh"
        atmosphere={<div className="atm-layer atm-workforce-field" aria-hidden="true" />}
      >
        <Workforce />
      </LazySection>

      <LazySection id="architecture" minH="70vh">
        <SystemDiagram />
      </LazySection>
      <LazySection id="security" minH="60vh">
        <Security />
      </LazySection>
      <LazySection id="spectrum" minH="80vh">
        <Spectrum />
      </LazySection>

      <LazySection
        id="cta"
        minH="60vh"
        atmosphere={<div className="atm-layer atm-cta-field" aria-hidden="true" />}
      >
        <CTABand />
      </LazySection>
    </main>
  );
}

export default function App() {
  return (
    <DemoModalProvider>
      <Navbar />
      <Home />
      <Footer />
    </DemoModalProvider>
  );
}
